import { useQuery } from "@apollo/client";
import { ChevronRight as ChevronRightIcon, Plus as PlusIcon } from "@tamagui/lucide-icons";
import { Avatar, Badge, Card, Divider, isWeb, ListItem, Modal, WithSeparator, YStack } from "@medbillai/ui";
import { useRouter } from "@medbillai/navigation/router";
import { accountsLinkWebAgentRelinkUrl, coverageUrl } from "@medbillai/utils/urls";
import { useAccountLinkDataStore } from "../../../lib/accounts-link/accountLinkStore";
import { analyticsService } from "../../../lib/analytics";
import { errorHandling } from "../../../lib/apollo/utils";
import { linksQuery } from "../../accounts-link/utils/queries";
import { type ProcessedAccountLink, useProcessedAccountLinks } from "../../accounts-link/utils/utils";
import { useCoveragePlanStore } from "../coveragePlanStore";
import { firstName, planSubtitle } from "../format";
import type { PlanSummary } from "../read/readCoverage";
import { PayerMark } from "./PlanHeader";
const NEEDS_REFRESH: ProcessedAccountLink["status"]["_tag"][] = ["EXPIRED", "FAILED", "CRAWL_TIMEOUT"];
type PlanSwitcherProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  current: PlanSummary;
  others: PlanSummary[];
  onAddPlan: () => void;
};
export function PlanSwitcher({
  open,
  onOpenChange,
  current,
  others,
  onAddPlan
}: PlanSwitcherProps) {
  const pickPlan = usePickPlan();
  const {
    push
  } = useRouter();
  const resetLink = useAccountLinkDataStore(state => state.reset);
  const setLinkExitUrl = useAccountLinkDataStore(state => state.setExitUrl);
  const {
    data
  } = useQuery(linksQuery, {
    skip: !open,
    context: errorHandling("caller")
  });
  const links = useProcessedAccountLinks(data?.me?.flexpaLinks ?? [], data?.me?.webAgentLinks ?? []);
  // Only a link that can resync has a relink route; Flexpa links can't.
  const stale = links.flatMap(l => l.relinkId && NEEDS_REFRESH.includes(l.status._tag) ? [{
    name: l.insuranceName,
    relinkId: l.relinkId
  }] : []);
  const pick = (plan: PlanSummary) => {
    if (plan !== current) {
      pickPlan(plan);
    }
    onOpenChange(false);
  };
  return <Modal open={open} onOpenChange={onOpenChange} headerProps={{
    title: "Insurance",
    variant: "titleLeft",
    // A full-screen web dialog needs its close button.
    hideCloseButton: !isWeb
  }} size="small" snapPoints={["auto"]} data-sentry-element="Modal" data-sentry-component="PlanSwitcher" data-sentry-source-file="PlanSwitcher.tsx">
      <YStack gap="$sp1.5" paddingBottom="$sp2" marginHorizontal="$-sp1" data-sentry-element="YStack" data-sentry-source-file="PlanSwitcher.tsx">
        <Card variant="elevated" size="large" padding="$sp1" data-sentry-element="Card" data-sentry-source-file="PlanSwitcher.tsx">
          <WithSeparator separator={<Divider indent="$sp1" />} data-sentry-element="WithSeparator" data-sentry-source-file="PlanSwitcher.tsx">
            {[current, ...others].map(plan => <PlanRow key={plan.membershipId} plan={plan} selected={plan === current} onPress={() => pick(plan)} />)}
            {stale.map(link => <StaleLinkRow key={link.relinkId} name={link.name} onPress={() => {
            analyticsService.trackEvent("CoverageLinkRefreshTapped", {
              linkId: link.relinkId
            });
            onOpenChange(false);
            resetLink();
            setLinkExitUrl(coverageUrl());
            push(accountsLinkWebAgentRelinkUrl({
              linkId: link.relinkId
            }));
          }} />)}
            <AddPlanRow onPress={() => {
            analyticsService.trackEvent("CoverageAddInsuranceTapped", {
              source: "switcher"
            });
            onAddPlan();
          }} data-sentry-element="AddPlanRow" data-sentry-source-file="PlanSwitcher.tsx" />
          </WithSeparator>
        </Card>
      </YStack>
    </Modal>;
}
export function usePickPlan() {
  const select = useCoveragePlanStore(state => state.select);
  return (plan: PlanSummary) => {
    analyticsService.trackEvent("CoveragePlanSwitched", {
      membershipId: plan.membershipId,
      health: plan.health === "ok" ? "ok" : "couldntConfirm"
    });
    select(plan.membershipId);
  };
}
export function PlanRow({
  plan,
  selected,
  onPress
}: {
  plan: PlanSummary;
  selected: boolean;
  onPress: () => void;
}) {
  return <ListItem
  // Inset and rounded so the hover fill and focus ring sit inside the card's corners.
  borderRadius="$br2" paddingHorizontal="$sp1" leftElement={<PayerMark name={plan.payerName} logoUrl={plan.logoUrl} />} title={plan.planName ?? plan.payerName} subtitle={[`${firstName(plan)}’s plan`, planSubtitle(plan)].filter(Boolean).join(" · ")} trailing={selected ? {
    type: "badge",
    bordered: false,
    status: "positive",
    children: "Selected"
  } : undefined} rightElement={plan.health !== "ok" ? <Badge size="small" bordered={false} status="warning">
            Couldn’t confirm
          </Badge> : undefined} onPress={onPress} data-sentry-element="ListItem" data-sentry-component="PlanRow" data-sentry-source-file="PlanSwitcher.tsx" />;
}
function AddPlanRow({
  onPress
}: {
  onPress: () => void;
}) {
  return <ListItem borderRadius="$br2" paddingHorizontal="$sp1" leftElement={<Avatar size="small" shape="square" icon={PlusIcon} />} title="Add another plan" rightElement={<ChevronRightIcon size="$iconXSmall" color="$iconMuted" />} onPress={onPress} data-sentry-element="ListItem" data-sentry-component="AddPlanRow" data-sentry-source-file="PlanSwitcher.tsx" />;
}
function StaleLinkRow({
  name,
  onPress
}: {
  name: string;
  onPress: () => void;
}) {
  return <ListItem borderRadius="$br2" paddingHorizontal="$sp1" leftElement={<PayerMark name={name} logoUrl={null} />} title={`${name} account`} rightElement={<Badge size="small" bordered={false} status="warning">
          Needs a refresh
        </Badge>} onPress={onPress} data-sentry-element="ListItem" data-sentry-component="StaleLinkRow" data-sentry-source-file="PlanSwitcher.tsx" />;
}